import { useState, useEffect } from 'react';
import { waitForHydrationComplete } from '@/utils/hydrationManager';

/**
 * Hook that provides hydration-safe state management
 * Prevents React hydration error #421 by ensuring consistent initial states
 * 
 * 🛡️ SYNC FIX: Now uses waitForHydrationComplete() instead of setTimeout(0)
 * This ensures all components using this hook wait for the same deterministic signal
 * from HydrationManager (triggered by entry-client.tsx after hydrateRoot() + double rAF)
 */
export function useHydrationSafe() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    
    // Wait for REAL hydration complete signal from entry-client.tsx
    // This resolves ONLY after hydrateRoot() + double requestAnimationFrame
    waitForHydrationComplete().then(() => {
      if (!cancelled) {
        setIsHydrated(true);
      }
    });
    
    return () => { cancelled = true; };
  }, []);

  return { isHydrated };
}

/**
 * Hook that provides stable values during hydration
 * @param serverValue Value to use during SSR and initial hydration
 * @param clientValue Value to use after hydration
 */
export function useHydrationSafeValue<T>(serverValue: T, clientValue: T): T {
  const { isHydrated } = useHydrationSafe();
  return isHydrated ? clientValue : serverValue;
}